import { ChangeEvent, useMemo, useState } from 'react';

import {
  FilterFormProps,
  FilterOption,
  isDynamicFilter,
  MultipleFilterProps,
} from '@/models/FilterConfig';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { FilterPopup } from './FilterPopup';
import FilterHeader from './FilterHeader';
import { BaseFilters } from '@/models/Service';
import CheckGroup, {
  CheckOption,
} from '@/components/ui/custom/dataInput/CheckGroup';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import useDebounce from '@/data/hook/useDebounce';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { LoadingIcon } from '../icons';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { useTranslation } from 'react-i18next';
import { Model as BaseModel } from '@/models/Model';

export default function MultipleFilter<
  Model extends BaseModel,
  FieldValue,
  Filter extends BaseFilters = BaseFilters,
>(props: MultipleFilterProps<Model, FieldValue, Filter> & FilterFormProps) {
  const {
    label,
    values = [],
    mode,
    setValue,
    name,
    service,
    withSearch,
    icon,
    className,
    hideClear,
  } = props;
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { debounce } = useDebounce();

  const isDynamic = isDynamicFilter(props);
  const filters: Filter = (isDynamic ? props.filters || {} : {}) as Filter;

  const { queryFn, queryKey } =
    service?.({ search: debouncedSearch, ...filters })._ctx.infinity || {};

  const {
    results,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isPreviousData: isLoading,
  } = useInfiniteService<Model>({
    keepPreviousData: true,
    enabled: isDynamic,
    queryFn,
    queryKey,
  });

  const options = useMemo<FilterOption<FieldValue>[]>(
    () => (mode === 'static' ? props.options : props.makeOptions(results)),
    [mode, props, results],
  );

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = e.target;

    const newValues = checked
      ? [...values, value]
      : values.filter(v => String(v) !== String(value));
    const newValuesMap = isDynamic ? newValues.map(Number) : newValues;
    setValue(name, newValuesMap as Array<string | number>);
  };

  function onClear() {
    setValue(name, undefined);
  }

  function onSearch(e: ChangeEvent<HTMLInputElement>) {
    if (!isDynamic) return;
    if (!withSearch) return;
    const value = e.target.value;
    setSearchValue(value);
    debounce(() => setDebouncedSearch(value));
  }

  const optionsGroup: CheckOption[] = options.map(({ label, value }) => ({
    id: value as string,
    value: value as string | number,
    label,
    checked: values.some(v => String(v) === String(value)),
  }));

  return (
    <FilterPopup
      label={label}
      reference={
        <FilterHeader
          label={label}
          notifications={values.length}
          icon={icon}
          className={{ icon: className?.icon, label: className?.label }}
        />
      }
      onClear={values.length && !hideClear ? onClear : undefined}
    >
      <ConditionalRenderer condition={isDynamic && props.withSearch}>
        <TextInput
          className={{ input: 'w-full' }}
          value={searchValue}
          placeholder={t('research')}
          icon={isLoading ? <LoadingIcon /> : <MagnifyingGlassIcon />}
          onChange={onSearch}
        />
      </ConditionalRenderer>

      <CheckGroup
        options={optionsGroup}
        onChange={handleChange}
        onReachEnd={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        scroll
      />
    </FilterPopup>
  );
}
